import { useMemo } from 'react';
import { WallPost } from './useWallFeed';

export interface HeatLevel {
  level: 0 | 1 | 2 | 3 | 4;
  name: 'normal' | 'warming' | 'hot' | 'fire' | 'viral';
  label: string;
  emoji: string;
  hasCombo: boolean;
}

// Thresholds for heat levels
const THRESHOLDS = {
  warming: { reactions: 10, comments: 5 },
  hot: { reactions: 25, comments: 15 },
  fire: { reactions: 50, comments: 30 },
  viral: { reactions: 100, comments: 50 },
};

export function calculateHeatLevel(
  reactionsCount: number,
  commentsCount: number
): HeatLevel {
  // Check for combo bonus (both reactions AND comments are significant)
  const hasReactionCombo = reactionsCount >= THRESHOLDS.warming.reactions && 
                           commentsCount >= THRESHOLDS.warming.comments;
  
  let level: 0 | 1 | 2 | 3 | 4 = 0;
  let name: HeatLevel['name'] = 'normal';
  let label = '';
  let emoji = '';
  
  // Check levels from highest to lowest
  if (reactionsCount >= THRESHOLDS.viral.reactions || commentsCount >= THRESHOLDS.viral.comments) {
    level = 4;
    name = 'viral';
    label = 'VIRAL';
    emoji = '🚀';
  } else if (reactionsCount >= THRESHOLDS.fire.reactions || commentsCount >= THRESHOLDS.fire.comments) {
    level = 3;
    name = 'fire';
    label = 'On Fire!';
    emoji = '🔥🔥';
  } else if (reactionsCount >= THRESHOLDS.hot.reactions || commentsCount >= THRESHOLDS.hot.comments) {
    level = 2;
    name = 'hot';
    label = 'Hot';
    emoji = '🔥';
  } else if (reactionsCount >= THRESHOLDS.warming.reactions || commentsCount >= THRESHOLDS.warming.comments) {
    level = 1;
    name = 'warming';
    label = 'Warming Up';
    emoji = '🌡️';
  }
  
  // Apply combo bonus - boost one level if both metrics are high
  if (hasReactionCombo && level > 0 && level < 4) {
    level = (level + 1) as 1 | 2 | 3 | 4;
    if (level === 4) {
      name = 'viral';
      label = 'VIRAL';
      emoji = '🚀';
    } else if (level === 3) {
      name = 'fire';
      label = 'On Fire!';
      emoji = '🔥🔥';
    } else if (level === 2) {
      name = 'hot';
      label = 'Hot';
      emoji = '🔥';
    }
  }
  
  return { level, name, label, emoji, hasCombo: hasReactionCombo && level > 0 };
}

export function usePostHeatLevel(post: WallPost): HeatLevel {
  return useMemo(() => {
    const totalReactions = post.reactions_count
      ? Object.values(post.reactions_count).reduce((sum, count) => sum + count, 0)
      : post.likes_count || 0;
    
    const commentsCount = post.comments_count || 0;
    
    return calculateHeatLevel(totalReactions, commentsCount);
  }, [post.reactions_count, post.likes_count, post.comments_count]);
}

export function getHeatGlowClass(level: number): string {
  switch (level) {
    case 1:
      return 'heat-warming';
    case 2:
      return 'heat-hot';
    case 3:
      return 'heat-fire';
    case 4:
      return 'heat-viral';
    default:
      return '';
  }
}
